import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, Select, Button, Alert, Card, Upload, message } from 'antd';
import { PlusOutlined, FileOutlined, DownloadOutlined } from '@ant-design/icons';
import { getProjects } from '../services/api';
import axios from 'axios';

const { Title, Text } = Typography;

interface Project {
  id: string;
  code: string;
  name: string;
}

const docTypeColor: { [key: string]: string } = {
  DRAWING: 'blue',
  METHOD_STATEMENT: 'purple',
  TEST_REPORT: 'green',
  PHOTO: 'cyan',
  VIDEO: 'magenta',
  SPECIFICATION: 'orange',
  CONTRACT: 'red',
  MOM_MINUTES: 'geekblue',
  OTHER: 'default',
};

const DocumentsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
        loadDocuments(response.data[0].id);
      }
    } catch (err) {
      setError('Failed to load projects.');
    }
  };

  const loadDocuments = async (projectId: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `https://q-pmc.com/api/v1/projects/${projectId}/documents/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDocuments(response.data);
    } catch (err) {
      setError('Failed to load documents.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    loadDocuments(value);
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string) => (
        <Text strong style={{ color: '#1B2A4A' }}>{text}</Text>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'document_type',
      key: 'document_type',
      render: (type: string) => (
        <Tag color={docTypeColor[type] || 'default'} icon={<FileOutlined />}>
          {type.replace('_', ' ')}
        </Tag>
      ),
    },
    {
      title: 'Revision',
      dataIndex: 'revision',
      key: 'revision',
      render: (rev: string) => <Tag color="blue">{rev}</Tag>,
    },
    {
      title: 'File Name',
      dataIndex: 'original_filename',
      key: 'original_filename',
      ellipsis: true,
    },
    {
      title: 'Size',
      dataIndex: 'file_size_bytes',
      key: 'file_size_bytes',
      render: (size: number) => {
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
      },
    },
    {
      title: 'Uploaded',
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
      render: (date: string) => new Date(date).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      }),
    },
    {
      title: 'Status',
      dataIndex: 'is_latest_revision',
      key: 'is_latest_revision',
      render: (latest: boolean) => latest
        ? <Tag color="green">Latest</Tag>
        : <Tag color="default">Superseded</Tag>,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
          Document Library
        </Title>
        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            value={selectedProject}
            onChange={handleProjectChange}
            style={{ width: 250 }}
            options={projects.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
          <Button type="primary" icon={<PlusOutlined />} style={{ background: '#1B2A4A' }}>
            Upload Document
          </Button>
        </div>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      <Card style={{ borderRadius: 12 }}>
        <Table
          columns={columns}
          dataSource={documents}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No documents found for this project.' }}
        />
      </Card>
    </div>
  );
};

export default DocumentsPage;